export type PlannedDuration =
  | 'ONE_MONTH'
  | 'THREE_MONTHS'
  | 'SIX_MONTHS'
  | 'CUSTOM';

export type RunningTimeline = {
  plannedDuration: PlannedDuration;
  plannedStartDate: string;
  plannedEndDate: string;
};

export const DURATION_OPTIONS: { value: PlannedDuration; label: string }[] = [
  { value: 'ONE_MONTH', label: '1 month' },
  { value: 'THREE_MONTHS', label: '3 months' },
  { value: 'SIX_MONTHS', label: '6 months' },
  { value: 'CUSTOM', label: 'Custom' },
];

export function durationLabel(value: PlannedDuration): string {
  return (
    DURATION_OPTIONS.find((opt) => opt.value === value)?.label ?? value
  );
}

export function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function formatDisplayDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function addMonthsIso(fromIso: string, months: number): string {
  const [y, m, d] = fromIso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setMonth(date.getMonth() + months);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Same idea as BE TimelineService.resolveEndDate (preview for mock). */
export function resolveEndDate(
  plannedDuration: PlannedDuration,
  customEndDate: string,
  startIso: string = todayIsoDate()
): string | null {
  switch (plannedDuration) {
    case 'ONE_MONTH':
      return addMonthsIso(startIso, 1);
    case 'THREE_MONTHS':
      return addMonthsIso(startIso, 3);
    case 'SIX_MONTHS':
      return addMonthsIso(startIso, 6);
    case 'CUSTOM':
      return customEndDate.trim() || null;
  }
}

export function getDurationError(
  plannedDuration: PlannedDuration | '',
  customEndDate: string
): string | null {
  if (!plannedDuration) return 'Select a planned duration.';
  if (plannedDuration === 'CUSTOM') {
    if (!customEndDate.trim()) return 'Select a planned end date.';
    if (customEndDate <= todayIsoDate()) {
      return 'End date must be in the future.';
    }
  }
  return null;
}

export function buildTimeline(
  plannedDuration: PlannedDuration,
  customEndDate: string
): RunningTimeline | null {
  if (getDurationError(plannedDuration, customEndDate)) return null;
  const plannedStartDate = todayIsoDate();
  const plannedEndDate = resolveEndDate(
    plannedDuration,
    customEndDate,
    plannedStartDate
  );
  if (!plannedEndDate) return null;
  return { plannedDuration, plannedStartDate, plannedEndDate };
}

export function timelineDayCount(
  plannedStartDate: string,
  plannedEndDate: string
): number | null {
  if (!plannedStartDate.trim() || !plannedEndDate.trim()) return null;
  if (plannedEndDate < plannedStartDate) return null;
  const [sy, sm, sd] = plannedStartDate.split('-').map(Number);
  const [ey, em, ed] = plannedEndDate.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((end.getTime() - start.getTime()) / msPerDay) + 1;
}
